import {
    AccessSegment,
    ColumnMetadata,
    ColumnTypes,
} from "~/components/DataGrid";
import { AssetColumns } from "~/modules/assets/types";
import { Emdash } from "~/components/Punctuation";
import { BadgeArrayCell } from "./BadgeArrayCell";
import { Link } from "~/components/Link";
import { ImageLoader } from "~/components/ImageLoader";
import { getAssetURL } from "~/utils/getAssetURL";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { RWA_COIN_ICON } from "~/consts";
import { AssetTableRowData } from "../types";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";

// // // //

export const UNDERLYING_ASSET_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.peggedAssetName,
    label: "Underlying Asset",
    type: ColumnTypes.string,
    sortsOn: AssetColumns.peggedAssetName,
    tooltip: CatalogReferenceDataSlugs.pegged_asset,
    width: 200,
    render: (item) => {
        if (item.isAsset) {
            if (!item.pegged_asset) return <Emdash />;

            return (
                <Link
                    href={getAssetURL({
                        asset: {
                            ticker: null,
                            slug: item.pegged_asset.slug,
                        },
                    })}
                    analyticsLabel={AssetUserEvents.ClickUnderlyingAssetLink}
                    analyticsEventProperties={{
                        asset: item.pegged_asset.name,
                    }}
                    variant="default"
                    className="flex flex-col overflow-hidden"
                >
                    <div className="w-full flex items-center gap-1.5">
                        <ImageLoader
                            src={item.pegged_asset.icon_url || RWA_COIN_ICON}
                            alt={`${item.pegged_asset.name} Icon`}
                            fallbackSrc={RWA_COIN_ICON}
                            className="h-5 w-5 min-w-5 rounded-full"
                        />
                        <div className="dg-overflow">
                            {item.pegged_asset.name}
                        </div>
                    </div>
                    <div className="font-light text-xs mt-0.5 text-muted-foreground dg-overflow">
                        {item.pegged_asset.type || <Emdash />}
                    </div>
                </Link>
            );
        }

        return null;
    },
};

export const UNDERLYING_ASSET_COLUMN_INLINE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    ...UNDERLYING_ASSET_COLUMN,
    render: (item) => {
        if (item.isAsset) {
            if (!item.pegged_asset) return <Emdash />;

            return (
                <Link
                    href={getAssetURL({
                        asset: {
                            ticker: null,
                            slug: item.pegged_asset.slug,
                        },
                    })}
                    analyticsLabel={AssetUserEvents.ClickUnderlyingAssetLink}
                    analyticsEventProperties={{
                        asset: item.pegged_asset.name,
                    }}
                    variant="default"
                    className="flex items-center gap-1.5 overflow-hidden"
                >
                    <ImageLoader
                        src={item.pegged_asset.icon_url || RWA_COIN_ICON}
                        alt={`${item.pegged_asset.name} Icon`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-5 w-5 min-w-5 rounded-full"
                    />
                    <div className="dg-overflow">{item.pegged_asset.name}</div>
                </Link>
            );
        }

        return null;
    },
};

// // // //

export const UNDERLYING_ASSETS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "Underlying",
    label: "Underlying Assets",
    tooltip: "Types of assets that back the asset",
    type: ColumnTypes.string,
    sortsOn: AssetColumns.underlyingAssetClasses,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    width: 190,
    render: (item) => {
        if (item.isAsset) {
            return (
                <BadgeArrayCell values={item.underlying_asset_classes || []} />
            );
        }

        return <Emdash />;
    },
};

export const UNDERLYING_ASSETS_COLUMN_RIGHT_JUSTIFY: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    ...UNDERLYING_ASSETS_COLUMN,
    render: (item) => {
        if (item.isAsset) {
            return (
                <BadgeArrayCell
                    values={item.underlying_asset_classes || []}
                    justify="right"
                />
            );
        }

        return <Emdash />;
    },
};
